// src/components/layout/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaLinkedinIn, FaYoutube, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import { NAVIGATION_LINKS, COMPANY_INFO } from '../../utils/constants';
import { 
  DIGITAL_COURSES, 
  ENGINEERING_COURSES, 
  GREEN_COURSES, 
  ENTREPRENEURSHIP_COURSES 
} from '../../data/coursesdata';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Get all course categories for the footer
  const courseCategories = [
    { id: 'engineering', title: ENGINEERING_COURSES.title },
    { id: 'digital', title: DIGITAL_COURSES.title },
    { id: 'green', title: GREEN_COURSES.title },
    { id: 'entrepreneurship', title: ENTREPRENEURSHIP_COURSES.title }
  ].filter(category => category.title); // Filter out any undefined categories
  
  return (
    <footer className="bg-primary-dark text-white pt-16 pb-8">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* About Section with Logo */}
          <div>
            <h3 className="text-xl font-bold mb-4">About Afrovate Tech Hub</h3>
            <p className="text-gray-300 mb-4">
              Youth-owned and youth-led social enterprise empowering Sierra Leone's youth through marketable digital skills.
            </p>
            <p className="text-gray-300 mb-6">
              <span className="font-medium text-white">Motto:</span> {COMPANY_INFO.motto}
            </p>
            
            {/* Logo */}
            <div className="mt-2 mb-4">
              <img 
                src="/images/logo/RobotoAfrovateLogo.png" 
                alt="Afrovate Tech Hub Logo" 
                className="h-40 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/logo/leotech-logo-fallback.png";
                  
                  // If fallback fails, show text logo
                  e.target.onerror = () => {
                    e.target.outerHTML = `<div class="text-white font-montserrat font-bold text-xl">LEOTECH</div>`;
                  };
                }}
              />
              <div className="text-xs text-gray-400 mt-1">EST. 2020</div>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-gray-300">
              {NAVIGATION_LINKS.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="hover:text-secondary transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Program Categories */}
          <div>
            <h3 className="text-xl font-bold mb-4">Our Programs</h3>
            <ul className="space-y-2 text-gray-300">
              {courseCategories.map((category) => (
                <li key={category.id}>
                  <Link 
                    to={`/programs?category=${category.id}`} 
                    className="hover:text-secondary transition-colors"
                  >
                    {category.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/programs" 
                  className="hover:text-secondary transition-colors"
                >
                  All Programs
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h3 className="text-xl font-bold mb-4">Contact Us</h3>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <FaMapMarkerAlt className="mt-1 mr-3 text-secondary" />
                <span>{COMPANY_INFO.contact.address}</span>
              </li>
              <li className="flex items-center">
                <FaPhone className="mr-3 text-secondary" />
                <span>{COMPANY_INFO.contact.phone}</span>
              </li>
              <li className="flex items-center">
                <FaEnvelope className="mr-3 text-secondary" />
                <a href={`mailto:${COMPANY_INFO.contact.email}`} className="hover:text-secondary transition-colors">
                  {COMPANY_INFO.contact.email}
                </a>
              </li>
            </ul>
            
            {/* Social Links */}
            <div className="flex space-x-4 mt-6">
              <a
                href="https://www.facebook.com/LeoTECH2021.1"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 p-2 rounded-full transition-colors"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>
              <a
                href="https://www.linkedin.com/company/leotech-sl-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-800 hover:bg-blue-900 p-2 rounded-full transition-colors"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn />
              </a>
              <a
                href="https://youtube.com/@leotechsl"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 hover:bg-red-700 p-2 rounded-full transition-colors"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>
            </div>
          </div>
        </div>
        
        {/* Newsletter */}
        <div className="border-t border-gray-700 pt-8 pb-4">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-xl font-bold mb-4">Subscribe to our Newsletter</h3>
            <p className="text-gray-300 mb-6">
              Stay updated with our latest programs, events, and success stories.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-grow px-4 py-2 rounded-md bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
                required
              />
              <button
                type="submit"
                className="bg-secondary hover:bg-secondary-dark text-white px-6 py-2 rounded-md font-medium transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-400">
            &copy; {currentYear} {COMPANY_INFO.name}. All rights reserved.
          </p>
          <div className="flex justify-center space-x-6 mt-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-gray-300 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-gray-300 transition-colors">
              Terms of Service
            </Link>
            <Link to="/sitemap" className="text-gray-400 hover:text-gray-300 transition-colors">
              Sitemap
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;